import { Checkbox, Combobox } from '@wonderschool/common-base-ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStudents } from '../../../students/studentsHooks';
import { isStudentArchived } from '../../../students/studentsUtils';
import { WsDropdownOptionType, WsDropdownProps } from '../widgets';

type DropdownOptionType = WsDropdownOptionType<string>;
type StudentDropdownProps = Omit<WsDropdownProps<string>, 'options'> & {
  filter?: (student: any) => boolean;
  kinderSystemsStudents?: boolean;
};

export const StudentDropdown: React.FC<StudentDropdownProps> = ({
  label,
  name,
  value,
  onChange,
  filter,
  'data-testid': dataTestId = 'student-select',
  kinderSystemsStudents = false,
}) => {
  const { t } = useTranslation();
  const [hideArchivedStudents, setHideArchivedStudents] = useState(false);
  const [archivedStudentsExists, setArchivedStudentsExists] = useState(false);
  const studentsData: any = useStudents({ filters: { hideArchivedStudents }, searchText: '' });

  useEffect(() => {
    const archivedExists = studentsData?.list?.some((student) => isStudentArchived(student));
    setArchivedStudentsExists(archivedExists);
    setHideArchivedStudents(archivedExists);
  }, [studentsData?.list]);

  const students = useMemo(() => {
    if (!filter) return studentsData?.listFiltered ?? [];

    const studentsList: any[] = studentsData?.listFiltered ?? [];
    if (kinderSystemsStudents) {
      return studentsList.filter((student) => student?.kinderSystems?.kinderConnectId);
    }
    return studentsList.filter((student) => filter(student) ?? true);
  }, [studentsData, filter, kinderSystemsStudents]);

  const options: DropdownOptionType[] = useMemo(() => {
    const optionsLocal = students?.map((student) => ({
      id: student.id,
      name: student.displayName ?? `${student.firstName} ${student.lastName}`,
    }));
    return optionsLocal ?? [];
  }, [students]);

  return (
    <div className="flex items-end">
      <Combobox
        label={label ?? t('shared.studentSelector.label')}
        placeholder={t('shared.studentSelector.placeholder')}
        name={name ?? 'student'}
        options={options}
        value={value}
        onChange={onChange}
        data-testid={dataTestId}
      />
      {archivedStudentsExists && (
        <div className="mb-2 ml-2">
          <Checkbox
            checked={!!hideArchivedStudents}
            name="hideArchivedStudents"
            label={t('students.hideArchivedStudents')}
            onChange={(e) => {
              setHideArchivedStudents(e.currentTarget.checked);
            }}
            data-testid="hide-archived-students"
          />
        </div>
      )}
    </div>
  );
};

export default StudentDropdown;
